/**
 * FoodPulseUrbanHarvestStory — long-form editorial page for the Urban
 * Harvest programme.
 *
 * Route: /food-pulse/urban-harvest/story
 *
 * This is the "Learn more" destination linked from the Urban Harvest
 * hero. It renders the full essay (title, hooks, structured sections)
 * with a strong CTA at the bottom to Register a Tree / Garden or
 * Suggest one nearby.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Trees, Sprout, HandHeart, MapPin, HelpCircle } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const PALETTE = {
  cream:  "#FBF7EF",
  ink:    "#1C3B1E",
  leaf:   "#3E7C4B",
  moss:   "#245635",
  amber:  "#E4A140",
  orange: "#EA580C",
  peach:  "#FFE9D0",
  sage:   "#DCEAD1",
};

export default function FoodPulseUrbanHarvestStory() {
  return (
    <div className="min-h-screen" style={{ background: PALETTE.cream, color: PALETTE.ink }}
         data-testid="urban-harvest-story-page">
      <SeoHead section="food-pulse"
               title="The Urban Harvest — Food Pulse"
               description="Turning hidden orchards and home gardens into a shared community resource." />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b"
               style={{ background: `linear-gradient(135deg, ${PALETTE.moss} 0%, ${PALETTE.leaf} 100%)`,
                        borderColor: "#0B2E12" }}>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-white">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-3"
               style={{ color: PALETTE.amber }}>
            <Trees className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
            Food Pulse · Urban Harvest · Article
          </div>
          <h1 className="font-display text-4xl sm:text-6xl leading-tight">
            The Urban Harvest
          </h1>
          <p className="mt-4 text-lg" style={{ color: PALETTE.peach }}>
            Turning Hidden Orchards and Home Gardens into a Shared Community Resource.
          </p>
          <div className="mt-6">
            <Link to="/food-pulse/urban-harvest#register"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2 font-semibold hover:shadow-lg transition-shadow"
                  style={{ background: PALETTE.amber, color: PALETTE.moss }}
                  data-testid="uh-story-cta-register">
              Register your tree / garden <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/food-pulse/urban-harvest#suggest"
                  className="ml-3 inline-flex items-center gap-2 rounded-full px-5 py-2 border-2 border-white/40 hover:bg-white/10 transition-colors"
                  data-testid="uh-story-cta-suggest">
              Suggest one nearby
            </Link>
          </div>
        </div>
      </section>

      {/* ── Article body ─────────────────────────────────────── */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-14 space-y-6 prose-lg leading-relaxed"
               data-testid="uh-story-article"
               style={{ fontSize: "1.05rem" }}>
        <P>
          Walk through almost any neighborhood and you'll notice something
          remarkable: fruit trees heavy with oranges, lemons, avocados,
          peaches, apples, figs, or persimmons. Vegetable gardens overflow
          with tomatoes, squash, herbs, peppers, and leafy greens. Yet every
          year, millions of pounds of perfectly edible food never make it to
          a table. Instead, it falls to the ground, spoils on the branch, or
          is thrown away because homeowners simply don't have the time, the
          means, or the opportunity to harvest and share it.
        </P>
        <P>
          At the same time, many families struggle to afford fresh produce.
          Food banks often face shortages of nutritious fruits and vegetables,
          while food prices continue to rise across many communities.
        </P>
        <PBold>
          This is not a production problem — it is a coordination and
          donation problem.
        </PBold>
        <P>FoodPulse believes technology can help solve it.</P>

        <H2>Introducing The Urban Harvest</H2>
        <P>
          The Urban Harvest is a new FoodPulse initiative designed to
          identify, document, and responsibly connect surplus fruits and
          vegetables growing in private homes, community gardens, schools,
          places of worship, farms, and neighborhood orchards with people
          and organizations that can use them.
        </P>
        <P>
          Through the <b>Home Fruit &amp; Vegetable Registry (HFVR)</b>,
          homeowners can voluntarily register fruit trees, vegetable gardens,
          herbs, berries, and other edible plants that regularly produce
          more than their household can consume.
        </P>
        <P>
          Instead of allowing valuable food to go to waste, FoodPulse helps
          create opportunities for responsible harvesting, sharing,
          donating, or selling within the local community.
        </P>

        <H2>A Hidden Food Supply</H2>
        <P>
          Across cities such as Los Angeles, Silicon Valley, Houston,
          Dallas, Austin, Miami, Orlando, Tampa, Phoenix, Seattle, Portland,
          Atlanta, New York, and hundreds of other communities, countless
          residential properties contain productive fruit trees and
          vegetable gardens.
        </P>
        <BulletList items={[
          "Citrus trees",
          "Avocado trees",
          "Apple trees",
          "Pear trees",
          "Peach and plum trees",
          "Fig trees",
          "Mango trees",
          "Guava trees",
          "Pomegranate trees",
          "Persimmon trees",
          "Mulberry trees",
          "Berry bushes",
          "Grapevines",
          "Vegetable gardens",
          "Herb gardens",
          "Community gardens",
          "School gardens",
        ]} />
        <P>
          Collectively, these represent an enormous source of fresh,
          nutritious food that is often overlooked.
        </P>

        <H2>Why So Much Food Is Lost</H2>
        <P>Many homeowners experience one or more common challenges:</P>
        <BulletList items={[
          "Trees produce more fruit than a family can consume.",
          "Harvesting tall trees requires equipment or assistance.",
          "Busy schedules leave little time for harvesting.",
          "Produce ripens while homeowners are traveling.",
          "Neighbors may not know the fruit is available.",
          "There is no easy way to coordinate donations or pickups.",
          "Concerns about privacy or liability discourage sharing.",
        ]} />
        <P>As a result, healthy food is lost despite strong local demand.</P>

        <H2>Building a Community Food Network</H2>
        <P>
          FoodPulse provides a platform where participants can choose how
          they wish to share their harvest.
        </P>
        <P>
          Homeowners remain in complete control of their listings and can
          decide whether they want to:
        </P>
        <BulletList items={[
          "Share produce with neighbors.",
          "Donate to local food banks or community organizations.",
          "Offer produce to volunteers for harvesting.",
          "Exchange produce with other gardeners.",
          "Sell surplus harvest locally.",
          "Simply document what grows on their property for future planning.",
        ]} />
        <P>
          Participation is always voluntary, and users determine what
          information they share.
        </P>

        <H2>Helping Volunteers Make a Difference</H2>
        <P>
          The Urban Harvest also creates opportunities for volunteers who
          enjoy gardening, harvesting, and community service.
        </P>
        <P>Volunteers may help:</P>
        <BulletList items={[
          "Harvest ripe fruit safely.",
          "Sort and package produce.",
          "Deliver donations.",
          "Assist elderly or disabled homeowners.",
          "Support neighborhood food drives.",
          "Reduce food waste after storms or peak harvest seasons.",
        ]} />
        <P>
          This strengthens community connections while ensuring more food
          reaches people who can use it.
        </P>

        <H2>Supporting Food Banks and Community Organizations</H2>
        <P>
          Food banks and nonprofit organizations often receive shelf-stable
          donations but have limited access to fresh fruits and vegetables.
        </P>
        <P>
          The Urban Harvest helps bridge this gap by creating opportunities
          for fresh, locally grown produce to reach:
        </P>
        <BulletList items={[
          "Food banks",
          "Community kitchens",
          "Schools",
          "Senior centers",
          "Shelters",
          "Faith-based organizations",
          "Neighborhood meal programs",
        ]} />
        <P>
          Fresh produce can have a meaningful impact on nutrition while
          reducing disposal costs and environmental waste.
        </P>

        <H2>A Living Food Map</H2>
        <P>
          The Home Fruit &amp; Vegetable Registry creates a growing inventory
          of edible plants within participating communities.
        </P>
        <P>Registered plants may include information such as:</P>
        <BulletList items={[
          "Fruit or vegetable type",
          "Variety",
          "General location",
          "Harvest season",
          "Typical production",
          "Current availability",
          "Photos",
          "Growing tips",
          "Care instructions",
          "Pollination information",
          "Sustainability notes",
        ]} />
        <P>
          This living database helps communities better understand their
          local food resources while encouraging biodiversity and responsible
          stewardship.
        </P>

        <H2>Reducing Food Waste</H2>
        <P>
          Every piece of fruit that is harvested instead of discarded
          represents:
        </P>
        <BulletList items={[
          "Less food waste",
          "Better nutrition",
          "Reduced methane emissions from landfills",
          "Greater appreciation for local agriculture",
          "Stronger neighborhood resilience",
        ]} />
        <P>
          Even small contributions from thousands of households can
          collectively recover significant amounts of fresh food each year.
        </P>

        <H2>Education and Citizen Science</H2>
        <P>The Urban Harvest is also an educational resource.</P>
        <P>Participants can learn about:</P>
        <BulletList items={[
          "Fruit tree care",
          "Vegetable gardening",
          "Seasonal planting",
          "Composting",
          "Pollinators",
          "Soil health",
          "Water conservation",
          "Climate resilience",
          "Sustainable food production",
        ]} />
        <P>
          Communities can document harvests over time, observe seasonal
          changes, and better understand how local growing conditions evolve.
        </P>

        <H2>Strengthening Local Resilience</H2>
        <P>
          Communities become stronger when they understand and value the
          resources already around them.
        </P>
        <P>
          The Urban Harvest encourages neighbors to work together, reduce
          waste, improve nutrition, and build stronger local food networks
          that can respond during periods of economic hardship, supply chain
          disruptions, or natural disasters.
        </P>
        <P>It is not simply about collecting fruit.</P>
        <PBold>It is about reconnecting communities with local food.</PBold>

        <H2>Looking Ahead</H2>
        <P>
          FoodPulse envisions The Urban Harvest growing into a global
          initiative where millions of households voluntarily document
          surplus fruits and vegetables, helping communities recover
          nutritious food that might otherwise be wasted.
        </P>
        <P>
          By combining technology, local knowledge, community participation,
          and responsible stewardship, FoodPulse hopes to make every
          backyard orchard, garden, and fruit tree part of a smarter, more
          sustainable food ecosystem.
        </P>

        <div className="mt-10 rounded-2xl border-2 p-6 sm:p-8"
             style={{ borderColor: PALETTE.leaf, background: PALETTE.sage }}
             data-testid="uh-story-closer">
          <div className="font-display text-2xl leading-snug" style={{ color: PALETTE.moss }}>
            Every tree has a story.
            <br />Every garden has potential.
            <br />Every harvest can nourish someone.
          </div>
          <p className="mt-3 text-sm opacity-80">
            Together, we can transform hidden abundance into shared
            opportunity — one neighborhood, one harvest, and one community
            at a time.
          </p>
        </div>
      </article>

      {/* ── Final CTAs ───────────────────────────────────────── */}
      <section className="border-t"
               style={{ background: PALETTE.ink, borderColor: "#0B2E12", color: "#fff" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 grid gap-6 sm:grid-cols-2">
          <CtaCard
            icon={Sprout}
            title="Register your tree or garden"
            body="Put your backyard orchard or vegetable patch on the community map. You decide every season what to share."
            to="/food-pulse/urban-harvest#register"
            cta="Register now"
            testid="uh-story-cta-register-2"
          />
          <CtaCard
            icon={MapPin}
            title="Suggest a tree or garden nearby"
            body="Spotted a productive tree or garden in your neighborhood? Tell us where it is — we'll send a kind invitation. Comments in any language."
            to="/food-pulse/urban-harvest#suggest"
            cta="Suggest one"
            testid="uh-story-cta-suggest-2"
          />
        </div>
      </section>
    </div>
  );
}


/* ── Presentational helpers ────────────────────────────────────── */
function P({ children }) {
  return <p style={{ color: PALETTE.ink }}>{children}</p>;
}

function PBold({ children }) {
  return <p className="font-semibold" style={{ color: PALETTE.moss }}>{children}</p>;
}

function H2({ children }) {
  return (
    <h2 className="font-display text-2xl sm:text-3xl mt-8 mb-2"
        style={{ color: PALETTE.moss }}>
      {children}
    </h2>
  );
}

function BulletList({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1" style={{ color: PALETTE.ink }}>
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
  );
}

function CtaCard({ icon: Icon, title, body, to, cta, testid }) {
  return (
    <div className="rounded-2xl p-5 border"
         style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.1)" }}
         data-testid={testid}>
      <Icon className="w-6 h-6 mb-2" style={{ color: PALETTE.amber }} />
      <div className="font-display text-xl mb-1">{title}</div>
      <p className="text-sm opacity-80 mb-4">{body}</p>
      <Link to={to}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-semibold"
            style={{ background: PALETTE.amber, color: PALETTE.moss }}>
        {cta} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
